import * as joint from '@joint/core';
import { ConnectorStyle, FlowType, LinkTypeColor, FlowTypeInfo } from './types';

export class DefaultShapeColors {
    static readonly PrimaryColor: string = '#000000';
    static readonly SecondaryColor: string = '#0D838F';
    static readonly EmphasizedColor: string = '#87BC24';
    static readonly ThirdPartyColor: string = '#52575B';
}

// Icon label size
export const IconLabelSize = { width: 32, height: 32 };

export const linkTypeColors: readonly LinkTypeColor[] = [
    { type: 'PhysicalLink', color: '#CE1210' },
    { type: 'LegalLink', color: '#99C13C' },
    { type: 'PaymentLink', color: '#9B9B9D' },
    { type: 'ServiceLink', color: '#000000' },
    { type: 'IPTransferLink', color: '#FFA500' },
    { type: 'IPLicenseLink', color: '#8E25AB' },
    { type: 'AgreementLink', color: '#256F8C' },
    { type: 'MiscellaneousLink', color: '#EB8100' },
    { type: 'OwnershipLink', color: '#000000' }
];

export function getFlowTypeInfo(flowType: FlowType): FlowTypeInfo {
    switch (flowType) {
        case FlowType.Physical:
            return { flowType, linkClassName: 'PhysicalLink', color: '#CE1210', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.Legal:
            return { flowType, linkClassName: 'LegalLink', color: '#99C13C', isDashed: true, isThin: false, hasArrowhead: true };
        case FlowType.Payment:
            return { flowType, linkClassName: 'PaymentLink', color: '#9B9B9D', isDashed: true, isThin: false, hasArrowhead: true };
        case FlowType.Service:
            return { flowType, linkClassName: 'ServiceLink', color: '#000000', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.IPTransfer:
            return { flowType, linkClassName: 'IPTransferLink', color: '#FFA500', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.IPLicense:
            return { flowType, linkClassName: 'IPLicenseLink', color: '#8E25AB', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.Agreement:
            return { flowType, linkClassName: 'AgreementLink', color: '#256F8C', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.Miscellaneous:
            return { flowType, linkClassName: 'MiscellaneousLink', color: '#EB8100', isDashed: false, isThin: false, hasArrowhead: true };
        case FlowType.Ownership:
            return { flowType, linkClassName: 'OwnershipLink', color: '#000000', isDashed: false, isThin: true, hasArrowhead: false };
        default:
            return { flowType, linkClassName: 'PhysicalLink', color: '#CE1210', isDashed: false, isThin: false, hasArrowhead: true };
    }
}

// Default connector style for links (paper-level default)
let defaultLinkConnectorStyle: ConnectorStyle = ConnectorStyle.Sharp;

export function setDefaultLinkConnectorStyle(style: ConnectorStyle): void {
    defaultLinkConnectorStyle = style;
}

export function getDefaultLinkConnectorStyle(): ConnectorStyle {
    return defaultLinkConnectorStyle;
}

export function toConnectorStyle(value: string | null | undefined): ConnectorStyle {
    const normalized = (value ?? '').toLowerCase();
    switch (normalized) {
        case 'rounded':
            return ConnectorStyle.Rounded;
        case 'curved':
            return ConnectorStyle.Curved;
        case 'sharp':
        default:
            return ConnectorStyle.Sharp;
    }
}

// Mixin to add connector style methods to link classes
export function applyConnectorStyleMixin(link: joint.shapes.standard.Link): void {
    const linkAny = link as any;
    
    linkAny.setConnectorStyle = function(style: ConnectorStyle): void {
        this.prop('connectorStyle', style);
        if (style === ConnectorStyle.Curved) {
            this.connector('smooth');
            return;
        }

        const cornerType = style === ConnectorStyle.Rounded ? 'cubic' : 'point';
        this.connector('straight', { cornerType });
    };
    
    linkAny.getConnectorStyle = function(): ConnectorStyle {
        return (this.prop('connectorStyle') as ConnectorStyle) || getDefaultLinkConnectorStyle();
    };
    
    // Apply default style on initialization
    const currentStyle = linkAny.prop('connectorStyle') as ConnectorStyle | undefined;
    if (!currentStyle) {
        linkAny.setConnectorStyle(getDefaultLinkConnectorStyle());
    } else {
        linkAny.setConnectorStyle(currentStyle);
    }
}
